/**
 * Authentication Service
 * Handles all auth-related operations with Supabase
 * 
 * Profile data comes from the 'users' table (extends auth.users).
 * Settings come from the 'user_settings' table.
 */
import { supabase } from '@/lib/supabase'
import type { Profile, UserSettings } from '@/shared/types'

export const authService = {
  // Auth operations
  async signUp(email: string, password: string) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    })
    return { data, error }
  },

  async signIn(email: string, password: string) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    return { data, error }
  },

  async signOut() {
    const { error } = await supabase.auth.signOut()
    return { error }
  },

  async getUser() {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser()
    return { user, error }
  },

  async getSession() {
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession()
    return { session, error }
  },

  onAuthStateChange(
    callback: (event: string, session: unknown) => void
  ) {
    return supabase.auth.onAuthStateChange(callback)
  },

  // Profile — from users table
  async getProfile(userId: string): Promise<{ data: Profile | null; error: unknown }> {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .maybeSingle()

    if (error) {
      console.warn('[authService] Failed to get profile from users table:', error.message)
    }

    // No row in users table OR query error → build from auth.users + auto-create row
    if (!data) {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return { data: null, error }

      // Try to auto-create the missing users row
      const newRow = {
        id: user.id,
        email: user.email ?? '',
        display_name: user.user_metadata?.display_name ?? user.email?.split('@')[0] ?? null,
        avatar_url: user.user_metadata?.avatar_url ?? null,
        role: 'user',
        status: 'active',
      }
      await supabase.from('users').upsert(newRow, { onConflict: 'id' }).select().maybeSingle()

      return {
        data: {
          ...newRow,
          youtube_connected: false,
          youtube_channel_id: null,
          youtube_channel_name: null,
          youtube_access_token: null,
          youtube_refresh_token: null,
          youtube_token_expires_at: null,
          max_channels: 3,
          max_videos_per_month: 50,
          max_storage_mb: 500,
          current_month_videos: 0,
          current_storage_mb: 0,
          usage_reset_at: new Date().toISOString(),
          email_verified_at: user.email_confirmed_at ?? null,
          created_at: user.created_at,
          updated_at: user.updated_at ?? user.created_at,
          deleted_at: null,
        } as Profile,
        error: null,
      }
    }

    return {
      data: {
        ...data,
        role: data.role ?? 'user',
      } as Profile,
      error: null,
    }
  },

  async updateProfile(userId: string, updates: Partial<Profile>) {
    const { data, error } = await supabase
      .from('users')
      .update({
        display_name: updates.display_name,
        avatar_url: updates.avatar_url,
        updated_at: new Date().toISOString(),
      })
      .eq('id', userId)
      .select()
      .single()

    if (error) return { data: null, error }
    return { data: data as Profile, error: null }
  },

  // Settings — from user_settings table
  async getSettings(userId: string): Promise<{ data: UserSettings | null; error: unknown }> {
    const { data, error } = await supabase
      .from('user_settings')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle()

    if (error) {
      console.warn('[authService] Failed to get settings:', error.message)
      return { data: null, error: null }
    }

    return { data: data as UserSettings | null, error: null }
  },

  async updateSettings(userId: string, updates: Partial<UserSettings>) {
    const { data, error } = await supabase
      .from('user_settings')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('user_id', userId)
      .select()
      .single()

    if (error) return { data: null, error }
    return { data: data as UserSettings, error: null }
  },
}
